import { createSlice } from '@reduxjs/toolkit';

import './MyAppointmentsActions';
import {
  addNotes,
  checkErpCredentials,
  getChildAppointments,
  getCustomerAppointmentsByLeadId,
  getLinkedProcessList,
  getMyAppointments,
  getMyAppointmentsNotes,
  saveErpCredentials,
} from './MyAppointmentsActions';

const initialState = {
  loading: {
    appointments: false,
    notes: false,
    addNotes: false,
    children: false,
    customerAppointmentsByLeadId: false,
    processLog: false,
    erpCredentials: false,
  },
  success: {
    appointments: false,
    notes: false,
    addNotes: false,
  },
  data: {
    appointments: [],
    statistics: {},
    total: 0,
    notes: [],
    children: [],
    customerAppointmentsByLeadId: [],
    processLog: [],
    hasErpCredentials: null,
  },
  error: {
    appointments: null,
    notes: null,
    addNotes: null,
    children: null,
    customerAppointmentsByLeadId: null,
    processLog: null,
    erpCredentials: null,
  },
};

const myAppointmentsSlice = createSlice({
  name: 'myAppointments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getMyAppointments.pending, (state) => {
        state.loading.appointments = true;
        state.error.appointments = null;
      })
      .addCase(getMyAppointments.fulfilled, (state, { payload }) => {
        state.loading.appointments = false;
        state.data.appointments = payload.data;
        state.data.statistics = payload.statistics;
        state.data.total = payload.total;
      })
      .addCase(getMyAppointments.rejected, (state, { payload }) => {
        state.loading.appointments = false;
        state.error.appointments = payload;
      })
      .addCase(getMyAppointmentsNotes.pending, (state) => {
        state.loading.notes = true;
        state.error.notes = null;
      })
      .addCase(getMyAppointmentsNotes.fulfilled, (state, { payload }) => {
        state.loading.notes = false;
        state.data.notes = payload;
      })
      .addCase(getMyAppointmentsNotes.rejected, (state, { payload }) => {
        state.loading.notes = false;
        state.error.notes = payload;
      })
      .addCase(getChildAppointments.pending, (state) => {
        state.loading.children = true;
        state.error.children = null;
      })
      .addCase(getChildAppointments.fulfilled, (state, { payload }) => {
        state.loading.children = false;
        state.data.children = payload;
      })
      .addCase(getChildAppointments.rejected, (state, { payload }) => {
        state.loading.children = false;
        state.error.children = payload;
      })
      .addCase(addNotes.pending, (state) => {
        state.loading.addNotes = true;
        state.error.addNotes = null;
      })
      .addCase(addNotes.fulfilled, (state) => {
        state.loading.addNotes = false;
      })
      .addCase(addNotes.rejected, (state, { payload }) => {
        state.loading.addNotes = false;
        state.error.addNotes = payload;
      })
      .addCase(getCustomerAppointmentsByLeadId.pending, (state) => {
        state.loading.customerAppointmentsByLeadId = true;
        state.error.customerAppointmentsByLeadId = null;
      })
      .addCase(getCustomerAppointmentsByLeadId.fulfilled, (state, { payload }) => {
        state.loading.customerAppointmentsByLeadId = false;
        state.data.customerAppointmentsByLeadId = payload;
      })
      .addCase(getCustomerAppointmentsByLeadId.rejected, (state, { payload }) => {
        state.loading.customerAppointmentsByLeadId = false;
        state.error.customerAppointmentsByLeadId = payload;
        state.data.customerAppointmentsByLeadId = [];
      })
      .addCase(getLinkedProcessList.pending, (state) => {
        state.loading.processLog = true;
        state.error.processLog = null;
      })
      .addCase(getLinkedProcessList.fulfilled, (state, { payload }) => {
        state.loading.processLog = false;
        state.data.processLog = payload ?? [];
      })
      .addCase(getLinkedProcessList.rejected, (state, { payload }) => {
        state.loading.processLog = false;
        state.data.processLog = [];
        state.error.processLog = payload;
      })
      .addCase(checkErpCredentials.pending, (state) => {
        state.loading.erpCredentials = true;
        state.error.erpCredentials = null;
      })
      .addCase(checkErpCredentials.fulfilled, (state, { payload }) => {
        state.loading.erpCredentials = false;
        state.data.hasErpCredentials = payload?.hasErpCredentials ?? true;
      })
      .addCase(checkErpCredentials.rejected, (state) => {
        state.loading.erpCredentials = false;
        state.data.hasErpCredentials = false;
      })
      .addCase(saveErpCredentials.pending, (state) => {
        state.loading.erpCredentials = true;
        state.error.erpCredentials = null;
      })
      .addCase(saveErpCredentials.fulfilled, (state) => {
        state.loading.erpCredentials = false;
        state.data.hasErpCredentials = true;
      })
      .addCase(saveErpCredentials.rejected, (state, { payload }) => {
        state.loading.erpCredentials = false;
        state.error.erpCredentials = payload;
      });
  },
});

export const selectLoading = (state) => state.myAppointments.loading?.appointments;
export const selectStatistics = (state) => state.myAppointments.data.statistics;
export const selectData = (state) => state.myAppointments.data.appointments;
export const selectTotal = (state) => state.myAppointments.data.total;

//notes
export const selectNotesLoading = (state) => state.myAppointments.loading?.notes;
export const selectNotesData = (state) => state.myAppointments.data.notes;
export const selectNotesError = (state) => state.myAppointments.error?.notes;

//add notes
export const selectAddNotesLoading = (state) => state.myAppointments.loading?.addNotes;
export const selectAddNotesError = (state) => state.myAppointments.error?.addNotes;

//child appointments
export const selectChildAppointmentsLoading = (state) =>
  state.myAppointments.loading?.children;
export const selectChildAppointmentsData = (state) => state.myAppointments.data.children;
export const selectChildAppointmentsError = (state) => state.myAppointments.error?.children;

//customer appointments by lead id
export const selectCustomerAppointmentsByLeadId = (state) =>
  state.myAppointments.data.customerAppointmentsByLeadId;
export const selectCustomerAppointmentsByLeadIdLoading = (state) =>
  state.myAppointments.loading?.customerAppointmentsByLeadId;

//process log
export const selectProcessLogLoading = (state) => state.myAppointments.loading?.processLog;
export const selectProcessLogData = (state) => state.myAppointments.data.processLog;
export const selectProcessLogError = (state) => state.myAppointments.error?.processLog;

//erp credentials
export const selectErpCredentialsLoading = (state) =>
  state.myAppointments.loading?.erpCredentials;
export const selectHasErpCredentials = (state) => state.myAppointments.data.hasErpCredentials;

export default myAppointmentsSlice.reducer;
